import React, { memo } from "react";
import type { Clip } from "../../../types";
import { Loader2, Lock, X } from "lucide-react";
import { THUMBNAIL_INTERVAL_S } from '../../../utils';

// Scissors SVG cursor (24x24, hotspot at blade tip)
const SCISSORS_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23fbbf24' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='6' cy='6' r='3'/%3E%3Ccircle cx='6' cy='18' r='3'/%3E%3Cline x1='20' y1='4' x2='8.12' y2='15.88'/%3E%3Cline x1='14.47' y1='14.48' x2='20' y2='20'/%3E%3Cline x1='8.12' y1='8.12' x2='12' y2='12'/%3E%3C/svg%3E") 12 12, crosshair`;

export const ClipElement = memo(function ClipElement({
    clip,
    zoom,
    toolMode,
    onSelectClip,
    selectedClipId,
    onClipDragStart,
    onDoubleClick,
    onDeleteClip
}: {
    clip: Clip;
    zoom: number;
    toolMode?: 'move' | 'cut';
    onSelectClip: (id: string | null, element: HTMLElement | null) => void;
    selectedClipId: string | null;
    onClipDragStart: (e: React.MouseEvent, clip: Clip, action: string) => void;
    onDoubleClick?: (clip: Clip) => void;
    onDeleteClip?: (clipId: string) => void;
}) {
    const isOutro = clip.clipRole === 'outro';
    const isSelected = selectedClipId === clip.id;
    const clipWidth = clip.duration * zoom;
    const isCutMode = toolMode === 'cut';

    let bgColor = 'bg-gray-700';
    if (clip.type === 'video') bgColor = 'bg-purple-900/50';
    else if (clip.type === 'audio') bgColor = 'bg-blue-900/50';
    else if (clip.type === 'text') bgColor = 'bg-green-900/50';
    else if (clip.type === 'image') bgColor = 'bg-yellow-900/50';
    else if (clip.type === 'zoom') bgColor = 'bg-teal-900/50';
    else if (clip.type === 'synthe') bgColor = 'bg-indigo-900/50';
    else if (clip.type === 'panel') bgColor = 'bg-pink-900/50';

    const handleSelect = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectClip(clip.id, e.currentTarget as HTMLElement);
    };

    return (
        <div
            className={`clip-element group absolute h-12 top-1 rounded-lg flex items-center overflow-hidden border-2 select-none ${bgColor} ${isSelected ? 'border-yellow-400 ring-2 ring-yellow-400 z-20' : 'border-transparent hover:border-gray-500 z-10'} ${isOutro ? 'cursor-default' : ''}`}
            style={{ left: `${clip.start * zoom}px`, width: `${clipWidth}px`, cursor: isCutMode && !isOutro ? SCISSORS_CURSOR : undefined }}
            onClick={handleSelect}
            onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(clip); }}
        >
            {!isOutro && (
                <div
                    className={`resize-handle-left absolute left-0 top-0 h-full w-2 ${isCutMode ? '' : 'cursor-ew-resize'} z-20`}
                    style={isCutMode ? { cursor: SCISSORS_CURSOR } : undefined}
                    onMouseDown={(e) => onClipDragStart(e, clip, isCutMode ? 'move' : 'resize-left')}
                />
            )}
            <div
                className={`w-full h-full ${isOutro ? 'cursor-default' : isCutMode ? '' : 'cursor-grab active:cursor-grabbing'}`}
                style={isCutMode && !isOutro ? { cursor: SCISSORS_CURSOR } : undefined}
                onMouseDown={isOutro ? undefined : (e) => onClipDragStart(e, clip, 'move')}
            >
                <div className="px-2 py-1 text-xs text-white truncate font-medium pointer-events-none flex items-center gap-2">
                    {(clip.isProcessingSubtitles || clip.isUploading) && <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />}
                    {isOutro && <Lock className="w-3 h-3 flex-shrink-0 text-gray-300" />}
                    <span className="truncate">{clip.name}</span>
                </div>
                {clip.type === 'video' && clip.thumbnails && (
                    <div className="absolute inset-0 flex pointer-events-none">
                        {clip.thumbnails.map((thumb, i) => (
                            <img key={i} src={thumb} className="h-full object-cover" style={{ width: `${THUMBNAIL_INTERVAL_S * zoom}px` }} alt="" loading="lazy" />
                        ))}
                    </div>
                )}
                {clip.type === 'audio' && clip.waveform && clip.waveform.length > 0 && (
                    <div className="absolute inset-0 flex items-center px-1 pointer-events-none">
                        <div className="w-full h-3/4 flex items-center gap-px">
                            {clip.waveform.map((val, i) => (
                                <div key={i} className="bg-blue-300/70" style={{ width: `${100 / (clip.waveform!.length || 1)}%`, height: `${val * 100}%` }} />
                            ))}
                        </div>
                    </div>
                )}
                {clip.type === 'text' && (
                    <div className="absolute inset-0 flex items-center justify-center p-1 pointer-events-none">
                        <p className="text-white text-center text-sm italic line-clamp-2">{clip.text}</p>
                    </div>
                )}
            </div>
            {!isOutro && (
                <div
                    className={`resize-handle-right absolute right-0 top-0 h-full w-2 ${isCutMode ? '' : 'cursor-ew-resize'} z-20`}
                    style={isCutMode ? { cursor: SCISSORS_CURSOR } : undefined}
                    onMouseDown={(e) => onClipDragStart(e, clip, isCutMode ? 'move' : 'resize-right')}
                />
            )}
            {!isOutro && onDeleteClip && (
                <button
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-100 transition-opacity z-30 shadow-lg"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDeleteClip(clip.id);
                    }}
                    title="Supprimer"
                >
                    <X className="w-3 h-3 text-white" />
                </button>
            )}
        </div>
    );
});
